import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Pencil, ArrowUp, ArrowDown } from "lucide-react";
import ImageUpload from "./ImageUpload";
import DocumentUpload from "./DocumentUpload";
import SlotsEditor from "./SlotsEditor";

/**
 * Generic resource editor.
 * Props:
 *  - resource: backend resource key (e.g. "tarifs")
 *  - title, subtitle
 *  - fields: [{ key, label, type ('text'|'textarea'|'image'|'select'), options?, placeholder?, full? }]
 *  - emptyItem: default object for new item
 *  - renderPreview?(item) optional preview node
 *  - groupByField?: optional field key to group items by (e.g., "category")
 *  - groupByLabel?: optional fn(rawKey) → displayed label (defaults to identity)
 */
const ResourceEditor = ({ resource, title, subtitle, fields, emptyItem, renderPreview, groupByField, groupByLabel }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item being edited (null = none, 'new' = creating)
  const [draft, setDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${resource}`);
      setItems(data || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const startEdit = (it) => { setEditing(it.id); setDraft({ ...it }); };
  const startNew = () => { setEditing("new"); setDraft({ ...emptyItem }); };
  const cancel = () => { setEditing(null); setDraft({}); };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post(`/admin/${resource}`, draft);
        toast.success("Élément créé");
      } else {
        await api.put(`/admin/${resource}/${editing}`, draft);
        toast.success("Élément mis à jour");
      }
      cancel();
      load();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success("Supprimé");
      load();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const move = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= items.length) return;
    const reordered = [...items];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, removed);
    setItems(reordered);
    try {
      await api.post(`/admin/${resource}/reorder`, reordered.map((i) => i.id));
    } catch {
      toast.error("Erreur de réordonnancement");
      load();
    }
  };

  const renderField = (f) => {
    const val = draft[f.key] ?? "";
    const onChange = (v) => setDraft({ ...draft, [f.key]: v });
    const base = "w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush";
    if (f.type === "image") {
      return <ImageUpload value={val} onChange={onChange} placeholder={f.placeholder} />;
    }
    if (f.type === "document") {
      return <DocumentUpload value={val} onChange={onChange} placeholder={f.placeholder} />;
    }
    if (f.type === "slots") {
      return <SlotsEditor value={val || []} onChange={onChange} teachers={f.teachers || []} />;
    }
    if (f.type === "textarea") {
      return <textarea value={val} onChange={(e) => onChange(e.target.value)} rows={f.rows || 4} className={base} placeholder={f.placeholder} />;
    }
    if (f.type === "select") {
      return (
        <select value={val} onChange={(e) => onChange(e.target.value)} className={base}>
          <option value="">— Choisir —</option>
          {f.options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }
    if (f.type === "number") {
      return <input type="number" value={val} onChange={(e) => onChange(Number(e.target.value))} className={base} placeholder={f.placeholder} />;
    }
    return <input type="text" value={val} onChange={(e) => onChange(e.target.value)} className={base} placeholder={f.placeholder} />;
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">{title}</h1>
          {subtitle && <p className="text-stone-500 mt-1 text-sm sm:text-base">{subtitle}</p>}
        </div>
        {editing === null && (
          <button onClick={startNew} className="inline-flex items-center gap-2 bg-coral text-white px-4 py-2 rounded-full text-sm font-medium shrink-0">
            <Plus size={16} /> Ajouter
          </button>
        )}
      </div>

      {editing !== null && (
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 my-5 border-2 border-blush-light">
          <h3 className="font-serif-display text-xl text-stone-800 mb-4">{editing === "new" ? "Nouvel élément" : "Modifier"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">{f.label}</label>
                {renderField(f)}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <button onClick={save} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2 rounded-full text-sm"><Save size={16} /> Enregistrer</button>
            <button onClick={cancel} className="inline-flex items-center gap-2 bg-stone-100 text-stone-700 px-5 py-2 rounded-full text-sm"><X size={16} /> Annuler</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-stone-500 p-8 text-center">Chargement...</div>
      ) : (
        <div className="space-y-3 mt-5">
          {items.length === 0 && <div className="bg-white rounded-2xl p-8 text-center text-stone-500">Aucun élément.</div>}
          {groupByField ? (
            (() => {
              const groups = {};
              items.forEach((it, originalIdx) => {
                const key = it[groupByField] || "Autres";
                if (!groups[key]) groups[key] = [];
                groups[key].push({ it, originalIdx });
              });
              return Object.entries(groups).map(([groupName, groupItems]) => (
                <div key={groupName} className="space-y-2">
                  <div className="text-xs uppercase tracking-widest text-deep-rose font-semibold pt-2 px-1">{groupByLabel ? groupByLabel(groupName) : groupName}</div>
                  {groupItems.map(({ it, originalIdx }) => (
                    <ItemRow key={it.id} it={it} idx={originalIdx} fields={fields} renderPreview={renderPreview}
                      onMove={move} onEdit={startEdit} onDel={del} itemsLength={items.length} />
                  ))}
                </div>
              ));
            })()
          ) : (
            items.map((it, idx) => (
              <ItemRow key={it.id} it={it} idx={idx} fields={fields} renderPreview={renderPreview}
                onMove={move} onEdit={startEdit} onDel={del} itemsLength={items.length} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ItemRow = ({ it, idx, fields, renderPreview, onMove, onEdit, onDel, itemsLength }) => (
  <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
    <div className="flex flex-col gap-1 shrink-0">
      <button onClick={() => onMove(idx, -1)} disabled={idx === 0} className="text-stone-400 hover:text-stone-700 disabled:opacity-30"><ArrowUp size={16}/></button>
      <button onClick={() => onMove(idx, 1)} disabled={idx === itemsLength - 1} className="text-stone-400 hover:text-stone-700 disabled:opacity-30"><ArrowDown size={16}/></button>
    </div>
    <div className="flex-1 min-w-0 overflow-hidden">
      {renderPreview ? renderPreview(it) : (
        <div className="min-w-0">
          <div className="font-serif-display text-stone-800 truncate">{it[fields[0]?.key] || "(sans titre)"}</div>
          <div className="text-stone-500 text-sm truncate">{it[fields[1]?.key] || ""}</div>
        </div>
      )}
    </div>
    <div className="flex gap-1.5 sm:gap-2 shrink-0">
      <button onClick={() => onEdit(it)} className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700"><Pencil size={16}/></button>
      <button onClick={() => onDel(it.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"><Trash2 size={16}/></button>
    </div>
  </div>
);

export default ResourceEditor;
